export interface RichTextEditorProps {
	value?: string;
	onChange?: (html: string) => void;
	placeholder?: string;
}

export interface ResponsiveSizes {
	xs: string;
	sm: string;
	md: string;
	lg: string;
	xl: string;
}

export interface SelectionState {
	isInTable: boolean;
	isImageSelected: boolean;
	currentImageLink: string | null;
	canMergeCells: boolean;
	canSplitCell: boolean;
	isInHeaderCell: boolean;
	currentColumnIndex: number | null;
	selectionType?: "heading" | "blockquote" | "paragraph" | "bulletList";
}

export interface ResponsiveTableData extends ResponsiveSizes {
	type: "percent" | "pixel";
	horizontalScroll: boolean;
	alignment: "left" | "center" | "right";
}

// Per-breakpoint width configuration (type + value for each breakpoint)
export interface BreakpointWidthConfig {
	type: "percent" | "pixel";
	value: string;
	alignment: "left" | "center" | "right";
	horizontalScroll: boolean;
	rowGap?: number;
}

export interface ResponsiveTableWidths {
	xs: BreakpointWidthConfig;
	sm: BreakpointWidthConfig;
	md: BreakpointWidthConfig;
	lg: BreakpointWidthConfig;
	xl: BreakpointWidthConfig;
}

// Column width configuration per breakpoint
export interface ResponsiveColumnWidths {
	xs: string;
	sm: string;
	md: string;
	lg: string;
	xl: string;
}

// Border configuration for individual sides
export interface BorderSideConfig {
	color: string;
	width: string;
}

export interface BorderConfig {
	top: BorderSideConfig;
	right: BorderSideConfig;
	bottom: BorderSideConfig;
	left: BorderSideConfig;
}

// Per-breakpoint column style configuration
export interface BreakpointColumnConfig {
	widthType: "percent" | "pixel";
	widthValue: string;
	textAlign: "left" | "center" | "right";
	verticalAlign: "top" | "middle" | "bottom";
	padding: PaddingValues;
	border?: BorderConfig;
}

export interface ResponsiveColumnStyles {
	xs: BreakpointColumnConfig;
	sm: BreakpointColumnConfig;
	md: BreakpointColumnConfig;
	lg: BreakpointColumnConfig;
	xl: BreakpointColumnConfig;
}

// Per-breakpoint row style configuration
export interface BreakpointRowConfig {
	heightType: "auto" | "pixel";
	heightValue: string;
	backgroundColor: string;
	textColor: string;
	border?: BorderConfig;
	gapAfter?: string; // Gap/spacing after this row in pixels
}

export interface ResponsiveRowStyles {
	xs: BreakpointRowConfig;
	sm: BreakpointRowConfig;
	md: BreakpointRowConfig;
	lg: BreakpointRowConfig;
	xl: BreakpointRowConfig;
}

// Per-breakpoint cell style configuration
export interface BreakpointCellConfig {
	backgroundColor: string;
	textColor: string;
	// Legacy single border (for backward compatibility)
	borderColor?: string;
	borderWidth?: string;
	// 4-sided border configuration
	border?: BorderConfig;
	textAlign: "left" | "center" | "right";
	verticalAlign: "top" | "middle" | "bottom";
	padding: PaddingValues;
	fontSize: string;
	fontWeight: string;
}

export interface ResponsiveCellStyles {
	xs: BreakpointCellConfig;
	sm: BreakpointCellConfig;
	md: BreakpointCellConfig;
	lg: BreakpointCellConfig;
	xl: BreakpointCellConfig;
}

// Column style configuration (legacy)
export interface ColumnStyleConfig {
	widths: ResponsiveColumnWidths;
	widthType: "percent" | "pixel";
	textAlign: "left" | "center" | "right";
	verticalAlign: "top" | "middle" | "bottom";
	padding: ResponsivePadding;
	backgroundColor?: string;
	textColor?: string;
	borderColor?: string;
}

// Responsive padding
export interface ResponsivePadding {
	xs: PaddingValues;
	sm: PaddingValues;
	md: PaddingValues;
	lg: PaddingValues;
	xl: PaddingValues;
}

export interface PaddingValues {
	top: string;
	right: string;
	bottom: string;
	left: string;
}

// Cell style configuration for responsive breakpoints
export interface ResponsiveCellStyle {
	xs: CellStyleValues;
	sm: CellStyleValues;
	md: CellStyleValues;
	lg: CellStyleValues;
	xl: CellStyleValues;
}

export interface CellStyleValues {
	backgroundColor?: string;
	textColor?: string;
	borderColor?: string;
	borderWidth?: string;
	padding?: PaddingValues;
	fontSize?: string;
	fontWeight?: string;
	textAlign?: "left" | "center" | "right";
}

// Breakpoint definitions
export const BREAKPOINTS = {
	xs: {
		min: 0,
		max: 1079,
		label: "Mobile (xs)",
		description: "340px - 1079px",
	},
	sm: {
		min: 1080,
		max: 1229,
		label: "Tablet (sm)",
		description: "1080px - 1229px",
	},
	md: {
		min: 1230,
		max: 1429,
		label: "Desktop (md)",
		description: "1230px - 1429px",
	},
	lg: {
		min: 1430,
		max: 1749,
		label: "Large (lg)",
		description: "1430px - 1749px",
	},
	xl: {
		min: 1750,
		max: Infinity,
		label: "Extra Large (xl)",
		description: "1750px+",
	},
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;
